import React, { useState, useEffect, useCallback } from 'react';
import { blogPosts } from './blogData';
import BlogCard from './BlogCard';
import ArticleModal from './ArticleModal';
import './Blog.css';

/**
 * Blog section: header and grid of cards. Clicking a card opens the article in a modal (card overlay).
 * Optional initialPostId opens that post's modal on mount (e.g. from /blog/:slug). Optional onCloseCallback runs when modal closes (e.g. navigate back).
 * 
 * URL hash support: visiting #blog-evm-storage opens that post directly.
 */
function BlogSection({ initialPostId, onCloseCallback }) {
  // Check URL hash on mount for direct blog links (e.g., #blog-evm-storage)
  const getPostFromHash = () => {
    const hash = window.location.hash;
    if (hash.startsWith('#blog-')) {
      const slug = hash.replace('#blog-', '');
      if (blogPosts[slug]) return slug;
    }
    return null;
  };

  const [selectedPostId, setSelectedPostId] = useState(() => {
    // Priority: URL hash > initialPostId prop
    const hashPost = getPostFromHash();
    if (hashPost) return hashPost;
    if (initialPostId && blogPosts[initialPostId]) return initialPostId;
    return null;
  });
  const postsWithSlug = Object.entries(blogPosts).map(([slug, post]) => ({
    ...post,
    slug,
  }));

  useEffect(() => {
    if (initialPostId && blogPosts[initialPostId]) {
      setSelectedPostId(initialPostId);
    }
  }, [initialPostId]);

  // Listen for hash changes (e.g., back/forward navigation)
  useEffect(() => {
    const handleHashChange = () => {
      const hashPost = getPostFromHash();
      setSelectedPostId(hashPost);
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const openPost = (slug) => {
    setSelectedPostId(slug);
    window.history.pushState(null, '', `#blog-${slug}`);
  };

  const closePost = useCallback(() => {
    setSelectedPostId(null);
    // Remove hash without triggering navigation
    window.history.pushState(null, '', window.location.pathname);
    onCloseCallback?.();
  }, [onCloseCallback]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') closePost();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [closePost]);

  return (
    <section className="about blog-section" id="blog">
      <header className="blog-section-header">
        <h2 className="blog-section-title">Blog Posts</h2>
        <p className="blog-section-subtitle">
          Technical writings & explorations in blockchain development
        </p>
      </header>
      <div className="blog-grid">
        {postsWithSlug.map((post, index) => (
          <BlogCard
            key={post.slug}
            post={post}
            index={index}
            onClick={openPost}
          />
        ))}
      </div>
      <ArticleModal postId={selectedPostId} onClose={closePost} />
    </section>
  );
}

export default BlogSection;
